import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { CheckCircle, Calendar, Clock, ArrowRight, Mail } from 'lucide-react';

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isGuest = searchParams.get('guest') === 'true';
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
    // pollPaymentStatus is declared below using function declaration syntax
    // and intentionally closes over sessionId/pollCount at call time, it
    // doesn't need to be in deps because it's a stable module function scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setTransaction(response.data);
        
        // For logged-in users, fetch booking details
        if (!isGuest && user) {
          try {
            const bookingsRes = await api.get('/bookings');
            const latestBooking = bookingsRes.data?.[0];
            if (latestBooking) {
              setBooking(latestBooking);
            }
          } catch (e) {
            console.error('Failed to fetch booking details:', e);
          }
        }
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <div className="max-w-lg mx-auto px-6 py-16">
        <div className="card-surface p-8 text-center" data-testid="booking-success-card">
          {status === 'checking' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#D4AF37] mx-auto mb-6"></div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">
                Processing Payment...
              </h1>
              <p className="text-stone-500">Please wait while we confirm your booking.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-medium text-[#1A1A1A] mb-2" data-testid="success-title">
                Booking Confirmed!
              </h1>
              <p className="text-stone-500 mb-8">
                Your appointment has been booked successfully. You'll receive a confirmation email shortly.
              </p>

              {booking && (
                <div className="p-6 bg-[#F9F6F0] rounded-lg mb-8 text-left">
                  <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">Booking Details</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-stone-500">Service</span>
                      <span className="font-medium text-[#1A1A1A]">{booking.service_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-stone-500">Provider</span>
                      <span className="font-medium text-[#1A1A1A]">{booking.provider_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-stone-500 flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Date
                      </span>
                      <span className="font-medium text-[#1A1A1A]">{booking.date}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-stone-500 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Time
                      </span>
                      <span className="font-medium text-[#1A1A1A]">{booking.time}</span>
                    </div>
                    <div className="pt-3 mt-3 border-t border-[#E6E4DD]">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-stone-500">
                          {booking.payment_type === 'deposit_50' ? 'Deposit Paid (50%)'
                           : booking.payment_type === 'minimum_deposit' ? 'Deposit Paid'
                           : 'Total Paid'}
                        </span>
                        <span className="text-xl font-semibold text-[#D4AF37]">£{(booking.charge_amount || booking.price).toFixed(2)}</span>
                      </div>
                      {booking.remaining_balance > 0 && (
                        <div className="flex items-center justify-between mt-2 text-sm">
                          <span className="text-stone-400">Remaining balance (due at appointment)</span>
                          <span className="font-medium text-stone-600">£{booking.remaining_balance.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Guest success message */}
              {isGuest && !booking && (
                <div className="p-6 bg-[#F9F6F0] rounded-lg mb-8 text-left">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <h3 className="font-medium text-[#1A1A1A] mb-1">Confirmation Sent!</h3>
                      <p className="text-sm text-stone-500">
                        We've sent your booking confirmation to your email. Please check your inbox for all the details.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                {!isGuest && user ? (
                  <Link to="/dashboard" className="flex-1">
                    <Button className="w-full btn-primary" data-testid="view-bookings-btn">
                      View My Bookings
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/register" className="flex-1">
                    <Button className="w-full btn-primary" data-testid="create-account-btn">
                      Create Account to Manage Bookings
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                )}
                <Link to="/discover" className="flex-1">
                  <Button variant="outline" className="w-full border-[#E6E4DD]">
                    Browse More Services
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="h-10 w-10 text-yellow-600" />
              </div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">
                Payment Pending
              </h1>
              <p className="text-stone-500 mb-6">
                We're still waiting for payment confirmation. Please check your email or contact support.
              </p>
              <Link to="/dashboard">
                <Button className="btn-primary">Go to Dashboard</Button>
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">
                Session Expired
              </h1>
              <p className="text-stone-500 mb-6">
                The payment session has expired. Please try booking again.
              </p>
              <Link to="/discover">
                <Button className="btn-primary">Browse Services</Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">
                Something went wrong
              </h1>
              <p className="text-stone-500 mb-6">
                There was an error checking your payment status. Please contact support.
              </p>
              <Link to="/dashboard">
                <Button className="btn-primary">Go to Dashboard</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
